import React, {forwardRef, useEffect, useImperativeHandle, useRef, useState} from "react"
import {
  Button,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  Step,
  Stepper,
  Typography
} from "@material-tailwind/react"
import {uploadData} from "../../api/DataAPI"
import {CloudArrowUpIcon} from "@heroicons/react/16/solid"

export const AddDataDialog = forwardRef((props, ref) => {
  const [open, setOpen] = useState(false)
  const [fileList, setFileList] = useState([])
  const [processing, setProcessing] = useState([])
  const [dragActive, setDragActive] = useState(false)

  const fileUploadRef = useRef()

  const handleOpen = () => setOpen(!open)

  useEffect(() => {
    setFileList([])
    setProcessing([])
  }, [open])

  useImperativeHandle(ref, () => ({
    openDialog() {
      handleOpen()
    }
  }))

  const handleUploadFiles = () => {
    fileList.forEach((item, idx) => {
      setProcessing(datas => ({
        ...datas,
        [idx]: 1
      }))

      uploadData(props.dataspace, item)
        .then(() => setProcessing(datas => ({
          ...datas,
          [idx]: 2
        })))
        .catch(err => console.log(err))
    })
  }

  const handleRemoveFile = (idx) => {
    const newArr = [...fileList];
    newArr.splice(idx, 1);
    setFileList(newArr);
  }

  const handleUploadClick = () => {
    fileUploadRef.current.click()
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    setFileList(fileList.concat(Array.from(e.dataTransfer.files)))
    setProcessing((Array(processing.length + e.dataTransfer.files.length)).fill(0))
  }

  const handleAppendFiles = (e) => {
    setFileList(fileList.concat(Array.from(e.target.files)))
    setProcessing(Array(e.target.files.length).fill(0))
  }

  return (
    <>
      <Dialog
        open={open}
        handler={handleOpen}
        animate={{
          mount: {scale: 1, y: 0},
          unmount: {scale: 0.9, y: -100},
        }}
      >
        <DialogHeader>Add Data</DialogHeader>

        <DialogBody>
          <div className="mb-3 w-96">

          </div>
          <div
            className={`${dragActive ? "bg-blue-100" : ""}  p-4 flex flex-col items-center gap-2 bg-violet-50 text-violet-500 rounded-lg hover:bg-violet-100 cursor-pointer justify-center`}
            onClick={handleUploadClick}
            onDragEnter={(e) => handleDragOver(e)}
            onDragOver={(e) => handleDragOver(e)}
            onDrop={(e) => handleDrop(e)}
            onDragLeave={(e) => handleDragLeave(e)}
          >
            <CloudArrowUpIcon className="w-6 h-6"/>
            <span>Choose some files to upload</span>
            <input type="file"
                   ref={fileUploadRef}
                   className="hidden"
                   multiple
                   onChange={(e) => handleAppendFiles(e)}/>
          </div>
          <div className="flex w-full font-bold pb-3 sticky py-3 border-2">
            <div className="flex justify-center text-center w-1/2">
              <p className={"my-auto mr-3"}>Data</p>
            </div>
            <div className="inline-block w-0.5 bg-neutral-100 opacity-100 dark:opacity-50"></div>
            <div className="my-auto text-center w-1/2">
              <p>Processing</p>
            </div>
          </div>
          <div className={"max-h-[350px] overflow-auto"}>
            {
              fileList.map((item, idx) => {
                return <div key={idx}>
                  <hr className="h-0.5 border-t-0 bg-neutral-100 opacity-100 dark:opacity-50"/>
                  <div className="flex w-full text-sm">
                    <div className="flex p-1 w-1/2">
                      <div className={"w-screen m-auto"}>
                        {item.name}
                      </div>
                      <div className={"w-fit m-auto"}>
                        <Button color={"red"} size="sm" onClick={() => handleRemoveFile(idx)}>Remove</Button>
                      </div>
                    </div>
                    <div className="w-1/2 text-center">
                      <Stepper
                        className={"px-14 py-6"}
                        activeStep={processing[idx]}
                        lineClassName="bg-black/50"
                        activeLineClassName="bg-black"
                      >
                        <Step className="h-4 w-4 !bg-blue-gray-50 text-black/75 cursor-pointer"
                              activeClassName="ring-0 !bg-black text-black"
                              completedClassName="!bg-black text-black">
                          <div className="absolute -bottom-[2.3rem] w-max text-center text-xs">
                            <Typography variant="h6" color="inherit">Init</Typography>
                          </div>
                        </Step>
                        <Step className="h-4 w-4 !bg-blue-gray-50 text-black/75 cursor-pointer"
                              activeClassName="ring-0 !bg-black text-black"
                              completedClassName="!bg-black text-black">
                          <div className="absolute -bottom-[2.3rem] w-max text-center text-xs">
                            <Typography variant="h6" color="inherit">Uploading</Typography>
                          </div>
                        </Step>
                        <Step className="h-4 w-4 !bg-blue-gray-50 text-black/75 cursor-pointer"
                              activeClassName="ring-0 !bg-black text-black"
                              completedClassName="!bg-black text-black">
                          <div className="absolute -bottom-[2.3rem] w-max text-center text-xs">
                            <Typography variant="h6" color="inherit">Complete</Typography>
                          </div>
                        </Step>
                      </Stepper>
                    </div>
                  </div>
                </div>
              })
            }
          </div>
        </DialogBody>

        <DialogFooter>
          <Button
            className="mr-2"
            variant="outlined"
            color="red"
            onClick={handleOpen}
          >
            <span>Close</span>
          </Button>
          <Button variant="gradient" color="green" onClick={handleUploadFiles}>
            <span>Confirm</span>
          </Button>
        </DialogFooter>
      </Dialog>
    </>
  )

})
